import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['calendarGrid', 'monthTitle', 'slotInput', 'confirmButton', 'timezoneDisplay', 'slotsList', 'scrollGradient']
  static classes = ['daySelected', 'dayAvailable', 'errorText', 'errorRetry']
  static values = { loadError: String, retryText: String }

  // The colours this controller applies used to be hard-coded Yseasonal ones
  // (bg-endeavour-500 and friends). Workays has no endeavour scale, and Tailwind only
  // generates classes it finds in a scanned file, so a class named only in here would
  // never be compiled for that bundle anyway. Each brand's markup supplies its own via
  // Stimulus class values; these defaults keep Yseasonal exactly as it was.
  //
  // Same shape as hour_form_controller.js, which solved this first.
  static DEFAULT_CLASSES = {
    daySelected: ['bg-endeavour-500', 'text-white'],
    dayAvailable: ['bg-endeavour-100', 'text-endeavour-700'],
    errorText: ['text-gray-500'],
    errorRetry: ['text-endeavour-600', 'underline']
  }

  selectedDate = null
  selectedSlot = null

  classesFor(name) {
    const capitalised = name.charAt(0).toUpperCase() + name.slice(1)
    if (this[`has${capitalised}Class`]) return this[`${name}Classes`]

    return this.constructor.DEFAULT_CLASSES[name]
  }

  connect() {
    // Initialize selected date from URL if present
    const urlParams = new URLSearchParams(window.location.search)
    const dateParam = urlParams.get('date')
    if (dateParam) {
      this.selectedDate = dateParam
    }
  }

  selectDate(event) {
    const button = event.currentTarget
    if (button.disabled) return

    const date = button.dataset.date
    const slotsUrl = button.dataset.slotsUrl

    // Update selected date
    this.selectedDate = date

    // Update visual selection
    this.updateDateSelection(button)

    // Clear any selected slot
    this.clearSlotSelection()

    // Fetch time slots via Turbo
    this.fetchTimeSlots(slotsUrl)
  }

  updateDateSelection(selectedButton) {
    const selected = this.classesFor('daySelected')
    const available = this.classesFor('dayAvailable')

    // Remove selection from all date buttons
    this.calendarGridTarget.querySelectorAll('button').forEach((btn) => {
      btn.classList.remove(...selected)
      if (btn.dataset.date && !btn.disabled) {
        const isAvailable = btn.classList.contains('font-medium')
        if (isAvailable) {
          btn.classList.add(...available)
        }
      }
    })

    // Add selection to clicked button
    selectedButton.classList.remove(...available)
    selectedButton.classList.add(...selected)
  }

  // No X-CSRF-Token here: this is a GET, Rails never verifies one on a GET, and reading it
  // unguarded is what used to kill the click. The read threw while these options were still
  // being assembled, so the request never went out and the catch below never ran. See #976.
  fetchTimeSlots(url) {
    // Use Turbo to fetch and update the time slots
    fetch(url, {
      headers: {
        Accept: 'text/vnd.turbo-stream.html',
      },
    })
      .then((response) => {
        // Turbo.renderStreamMessage renders nothing at all when handed an error page, so
        // without this the panel silently froze on the previously selected date. A booking
        // link expiring while the page is open is the ordinary way a candidate gets here.
        if (!response.ok) throw new Error(`Time slots request failed: ${response.status}`)

        return response.text()
      })
      .then((html) => {
        Turbo.renderStreamMessage(html)
      })
      .catch((error) => {
        console.error('Error fetching time slots:', error)
        this.renderSlotsError(() => this.fetchTimeSlots(url))
      })
  }

  selectSlot(event) {
    const button = event.currentTarget
    const slotData = button.dataset.slot

    // Update selected slot
    this.selectedSlot = slotData

    // Update visual selection
    this.updateSlotSelection(button)

    // Update hidden form field
    if (this.hasSlotInputTarget) {
      this.slotInputTarget.value = slotData
    }

    // Enable confirm button
    if (this.hasConfirmButtonTarget) {
      this.confirmButtonTarget.disabled = false
    }
  }

  updateSlotSelection(selectedButton) {
    // Remove selection from all slot buttons
    const container = selectedButton.closest('div').parentElement
    container.querySelectorAll('button[data-slot]').forEach((btn) => {
      btn.removeAttribute('data-selected')
    })

    // Add selection to clicked button
    selectedButton.setAttribute('data-selected', 'true')
  }

  clearSlotSelection() {
    this.selectedSlot = null

    // Clear hidden field
    if (this.hasSlotInputTarget) {
      this.slotInputTarget.value = ''
    }

    // Disable confirm button
    if (this.hasConfirmButtonTarget) {
      this.confirmButtonTarget.disabled = true
    }
  }

  // The candidate used to see nothing when a request failed: the only handling was
  // console.error, so a dead click was indistinguishable from a page that does nothing.
  //
  // The frame is looked up by id rather than held as a target because
  // turbo_stream.replace('time_slots') destroys and recreates it, and the replacement markup
  // carries no target attribute, so a target would work exactly once. navigateToMonth already
  // looks up booking_calendar the same way.
  //
  // Copy arrives as a Stimulus value and colours as class values because this app has no
  // JS-side i18n, this page is served in en, nl and de, and a class named only in here is
  // compiled into neither brand's bundle. Same reason as DEFAULT_CLASSES above.
  //
  // The bare layout utilities below (py-8, text-center, mt-2) are safe only because both
  // brands' _time_slots partials already carry them, so Tailwind emits them for each bundle.
  // Anything new belongs in a class value, not in here.
  renderSlotsError(retry) {
    const frame = document.getElementById('time_slots')
    if (!frame) return

    // Clear first, while the confirm button and hidden input still exist.
    this.clearSlotSelection()

    const wrapper = document.createElement('div')
    wrapper.className = 'py-8 text-center'

    const message = document.createElement('p')
    message.classList.add(...this.classesFor('errorText'))
    // textContent, not innerHTML: the string is translator-supplied and there is no reason to
    // make it a markup channel.
    message.textContent = this.loadErrorValue
    wrapper.appendChild(message)

    const button = document.createElement('button')
    button.type = 'button'
    button.classList.add('mt-2', ...this.classesFor('errorRetry'))
    button.textContent = this.retryTextValue
    button.addEventListener('click', retry)
    wrapper.appendChild(button)

    frame.replaceChildren(wrapper)
  }

  confirmBooking() {
    if (!this.selectedSlot) return

    // Submit the booking form
    const form = document.getElementById('booking_form')
    if (form) {
      form.submit()
    }
  }

  previousMonth(event) {
    const button = event.currentTarget
    if (button.disabled) return
    this.navigateToMonth(button.dataset.calendarUrl)
  }

  nextMonth(event) {
    const button = event.currentTarget
    if (button.disabled) return
    this.navigateToMonth(button.dataset.calendarUrl)
  }

  navigateToMonth(url) {
    const frame = document.getElementById('booking_calendar')
    if (frame) {
      // GET as well, so no token header. Same reason as fetchTimeSlots.
      fetch(url, {
        headers: {
          Accept: 'text/vnd.turbo-stream.html',
        },
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Calendar request failed: ${response.status}`)

          return response.text()
        })
        .then((html) => {
          Turbo.renderStreamMessage(html)
          // Clear slot selection when changing months
          this.clearSlotSelection()
        })
        .catch((error) => {
          console.error('Error navigating to month:', error)
          this.renderSlotsError(() => this.navigateToMonth(url))
        })
    }
  }

  checkScrollPosition() {
    if (!this.hasSlotsListTarget || !this.hasScrollGradientTarget) return

    const list = this.slotsListTarget
    const gradient = this.scrollGradientTarget

    // Check if content is scrollable at all
    const isScrollable = list.scrollHeight > list.clientHeight
    const isAtBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 5

    // Hide gradient if not scrollable or if scrolled to bottom
    gradient.classList.toggle('opacity-0', !isScrollable || isAtBottom)
  }

  // Called when slots list target is connected (after Turbo stream update)
  slotsListTargetConnected() {
    // Small delay to ensure DOM is fully rendered
    requestAnimationFrame(() => {
      this.checkScrollPosition()
    })
  }
}
